import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { getServicesManager } from '../../../../services';
import { startLoading, startDebouncing, stopDebouncing, sessionsLoaded, loadingFailed, clearSessions, updateHasMore, updateForceRefresh, toReduxSafeSessionListResponses } from './list-slice';
import { enrichContacts, selectPersonByNumber, EnrichStateResult } from '../enrich/slice';
import { SelectColumn, SessionListResponse, SessionListV5Params } from '../../../../interfaces/sessions';
import { BackendTables, OBJECTION_COLUMNS } from '../../../../interfaces/db';
import { CallMetricColumns, CallbackRequestColumns, MessagingDataColumns, PROSPECT_INFO_COLUMNS, ReviewStatusColumns, SESSION_METRICS_COLUMNS, SessionV2Columns, CustomMetricColumns, CustomDetailColumns, SessionAgentsColumns, SessionStatesColumns } from '../../../../interfaces/table';
import { SessionDetailView, SessionHistoryTabs } from '../../../../components/History/views';
import { DAY_TO_MS } from '../../../../cfg/const';
import { TypedResultMap } from '../../../../interfaces/sessions';
import { useSessionItems } from './session-items-hook';
import { SessionListProps } from './cfg';

// Constants
const DEFAULT_PAGE_SIZE = 50;
const MAX_BACKOFF_RETRIES = 2; // Max number of additional backoff fetches after initial fetch
const MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_DATE_LOOKBACK_MONTHS = 6; // Maximum lookback period for auto-loading
export const ABSOLUTE_MAX_LOOKBACK_MONTHS = 12; // Hard limit even for manual lookback
const LOAD_MORE_MONTHS_EXTENSION = 2; // Number of months to extend when manually loading more
const BACKOFF_MULTIPLIERS = [1.5, 2]; // Reduced backoff multipliers (was [2, 3])
const BACKOFF_BASE_DELAY = 500; // Base delay for backoff in ms

// Filter debounce constants
const FILTER_DEBOUNCE_MS = 350;
const KEY_STABILIZATION_MS = 100;

// Add this function before the useSessionFetch hook
function augmentSessionWithPersonData(session: SessionListResponse, enrichState: EnrichStateResult): SessionListResponse {
  if (!session.customer_address) return session;

  const person = selectPersonByNumber({ enrich: enrichState }, session.customer_address);
  if (!person) return session;

  // Create a new result map with the original data
  const newResult = new TypedResultMap(session.result);

  // Add PersonV2 fields to the result
  if (person.name) {
    newResult.set(PROSPECT_INFO_COLUMNS.PROSPECT_NAME, person.name);
  }
  if (person.title) {
    newResult.set(PROSPECT_INFO_COLUMNS.PROSPECT_TITLE, person.title);
  }
  if (person.platform_company_id) {
    newResult.set(PROSPECT_INFO_COLUMNS.PLATFORM_COMPANY_ID, person.platform_company_id);
  }
  if (person.platform_person_id) {
    newResult.set(PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID, person.platform_person_id);
  }

  return {
    ...session,
    result: newResult
  };
}

export function useSessionFetch(props: SessionListProps) {
  const { disableDebouncing } = props;
  const dispatch = useDispatch();
  const refreshCount = useSelector((state: RootState) => state.refresh.forceRefresh)
  const custom_definitions = useSelector((state: RootState) => state.customDefinitions.value);
  const enrichState = useSelector((state: RootState) => state.enrich);
  const has_custom_definitions = custom_definitions && custom_definitions.length > 0;

  // Use session-items-hook for all core logic and state management
  const sessionItems = useSessionItems(props)
  const sessionState = useSelector((state: RootState) => state.sessionList);
  const { 
    items: baseItems, 
    filter, 
    sessionKey, 
    getStartDate,
    getCurrentSession,
    updateSessionEnhancement,
    calculateMonthsSearched,
    needsMoreData,
    hasStrictStartDate
  } = sessionItems

  const { enforced_end, tab, view } = props;

  // Refs for managing debouncing and preventing race conditions
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const keyStabilizationRef = useRef<NodeJS.Timeout | null>(null);
  const currentKeyRef = useRef<string>('');
  const isFilterChangeInProgressRef = useRef<boolean>(false);

  // Refs for fetch management
  const previousKeyRef = useRef<string>('');
  const hasKeyChanged = useRef<boolean>(false);
  const isInitialMount = useRef<boolean>(true);
  const lastProcessedRefreshRef = useRef<number>(0);
  const isRefreshingRef = useRef<boolean>(false);
  const isAutoFetchingRef = useRef<boolean>(false);
  
  // Column definitions moved inside useMemo to prevent dependency issues

  const columns: SelectColumn[] = useMemo(() => {
    // Column definitions (keeping the existing complex column logic)
    const required_columns: SelectColumn[] = [
      {
        column_name: SessionV2Columns.SESSION_ID,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.STARTED_AT,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.AGENT_ADDRESS,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.CUSTOMER_ADDRESS,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.RESOURCE_ID,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.CONVERSATION_TYPE,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SessionV2Columns.SESSION_TYPE,
        table: BackendTables.SESSIONS_V2
      }, {
        column_name: SESSION_METRICS_COLUMNS.PLATFORM,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SessionAgentsColumns.CREATOR_AGENT_ID,
        table: BackendTables.SESSION_AGENTS
      }, {
        column_name: SessionAgentsColumns.FIRST_USER_ID,
        table: BackendTables.SESSION_AGENTS
      }, {
        column_name: SessionAgentsColumns.AGENT_TYPES_STR,
        table: BackendTables.SESSION_AGENTS
      }
    ];

    // Add session state columns for all calls
    const session_state_columns: SelectColumn[] = [
      {
        column_name: SessionStatesColumns.STATE_STR,
        table: BackendTables.SESSION_STATES
      }
    ];

    const review_columns: SelectColumn[] = [
      {
        column_name: ReviewStatusColumns.IS_OPEN,
        table: BackendTables.REVIEW_STATUSES
      }, {
        column_name: ReviewStatusColumns.OPENING_USER_ID,
        table: BackendTables.REVIEW_STATUSES
      }
    ];

    const prospect_columns: SelectColumn[] = [
      {
        column_name: PROSPECT_INFO_COLUMNS.PROSPECT_NAME,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.PROSPECT_TITLE,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.COMPANY_NAME,
        table: BackendTables.PROSPECT_INFO
      },{
        column_name: PROSPECT_INFO_COLUMNS.PLATFORM_COMPANY_ID,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.XREF_PERSON_ID,
        table: BackendTables.PROSPECT_INFO
      }, {
        column_name: PROSPECT_INFO_COLUMNS.XREF_PERSON_URL,
        table: BackendTables.PROSPECT_INFO
      }
    ];

    const call_columns: SelectColumn[] = [
      {
        column_name: CallMetricColumns.CALL_DURATION,
        table: BackendTables.CALL_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.IS_ANSWERED,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.TARGET_DURATION,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.BEST_COUNTERPART,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.USER_DISPOSITION,
        table: BackendTables.SESSION_METRICS
      }, {
        column_name: SESSION_METRICS_COLUMNS.USER_SENTIMENT,
        table: BackendTables.SESSION_METRICS
      }
    ];

    const callback_columns: SelectColumn[] = [
      {
        column_name: CallbackRequestColumns.CALLBACK_STATUS,
        table: BackendTables.CALLBACK_REQUEST_DETAILS
      }, {
        column_name: CallbackRequestColumns.CALLBACK_REASONING,
        table: BackendTables.CALLBACK_REQUEST_DETAILS
      }, {
        column_name: CallbackRequestColumns.CALLBACK_TIMING,
        table: BackendTables.CALLBACK_REQUEST_DETAILS
      }
    ];

    const objection_columns: SelectColumn[] = OBJECTION_COLUMNS.map((column) => ({
      column_name: column,
      table: BackendTables.SESSION_METRICS
    }));

    const meta_call_columns: SelectColumn[] = [{
      column_name: SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT,
      table: BackendTables.SESSION_METRICS
    }, {
      column_name: SESSION_METRICS_COLUMNS.USER_DISPOSITION,
      table: BackendTables.SESSION_METRICS
    }, {
      column_name: SESSION_METRICS_COLUMNS.USER_SENTIMENT,
      table: BackendTables.SESSION_METRICS
    }];

    const text_columns = [
      {
        column_name: MessagingDataColumns.AGENT_BODIES_STR,
        table: BackendTables.MESSAGING_DATA
      }, {
        column_name: MessagingDataColumns.AGENT_TIMES_STR,
        table: BackendTables.MESSAGING_DATA
      }, {
        column_name: MessagingDataColumns.CUSTOMER_BODIES_STR,
        table: BackendTables.MESSAGING_DATA
      }, {
        column_name: MessagingDataColumns.CUSTOMER_TIMES_STR,
        table: BackendTables.MESSAGING_DATA
      }, {
        column_name: MessagingDataColumns.MAX_CUSTOMER_AT,
        table: BackendTables.MESSAGING_DATA
      }
    ];

    const score_columns = [
      ...Object.values(CustomMetricColumns).map((column) => ({
        column_name: column,
        table: BackendTables.CUSTOM_METRICS
      })),
      ...Object.values(CustomDetailColumns).map((column) => ({
        column_name: column,
        table: BackendTables.CUSTOM_DETAILS
      }))
    ];

    const missing_columns = [
      {
        column_name: CallMetricColumns.FIRST_USER_JOINED_AT,
        table: BackendTables.CALL_METRICS
      }, {
        column_name: CallMetricColumns.IS_INBOUND,
        table: BackendTables.CALL_METRICS
      }
    ];

    const columns: SelectColumn[] = [...required_columns, ...session_state_columns, ...review_columns, ...prospect_columns, ...missing_columns];
    if (has_custom_definitions && (view === SessionDetailView.MAIN || view === SessionDetailView.ANALYSIS || view === SessionDetailView.ANALYTICS)) {
      columns.push(...score_columns);
    }
    switch (tab) {
      case SessionHistoryTabs.CALLS:
        columns.push(...call_columns, ...objection_columns);
        break;
      case SessionHistoryTabs.MISSED:
        columns.push(...call_columns);
      break;
        case SessionHistoryTabs.CALLBACKS:
        columns.push(...call_columns, ...callback_columns, ...objection_columns);
        break;
      case SessionHistoryTabs.TEXTS:
        columns.push(...text_columns);
        break;
      case SessionHistoryTabs.CHAT:
        columns.push(...text_columns);
        break;
      case SessionHistoryTabs.ALL_HISTORY:
        columns.push(...call_columns, ...callback_columns, ...objection_columns, ...meta_call_columns, ...text_columns);
        break;
      default:
        break;
    }
    return columns;
  }, [tab, has_custom_definitions, view]);

  // Prepare params for API request - SIMPLIFIED VERSION FROM fetch-old.tsx
  const getParams = useCallback((
    forceRefresh: boolean = false, 
    extendByMonths: number = 0,
    baseStartDate?: Date | null
  ): SessionListV5Params => {
    const session = getCurrentSession(sessionKey);
    const relevantItems = session.items;
    
    // Simple logic that WORKS from fetch-old.tsx
    let end: Date | undefined;
    if (forceRefresh) {
      // Use enforced_end if available, otherwise use current date
      end = enforced_end ?? new Date();
    } else if (extendByMonths > 0 && baseStartDate) {
      // When loading more historical data, use the previous start date as our end date
      end = baseStartDate;
    } else if (relevantItems && relevantItems.length > 0) {
      // Normal pagination - use last item's date
      end = relevantItems[relevantItems.length - 1].start;
    } else {
      // Use enforced_end if available, otherwise use current date
      end = enforced_end ?? new Date();
    }

    return { 
      'cnf': filter, 
      'select': columns,
      'start': getStartDate(extendByMonths, baseStartDate), 
      'end': end,
    };
  }, [sessionKey, columns, filter, getStartDate, enforced_end, getCurrentSession]);

  // Check if a date is within the allowed lookback window
  const isWithinDateLimit = useCallback((date: Date | null): boolean => {
    if (!date) return true;
    
    const endDate = enforced_end ?? new Date();
    const maxLookback = MAX_DATE_LOOKBACK_MONTHS * MONTH_IN_MS;
    
    return (endDate.getTime() - date.getTime()) <= maxLookback;
  }, [enforced_end]);

  // The main fetch function
  const fetchSessions = useCallback(async (
    options: {
      isLoadMore?: boolean; 
      forceRefresh?: boolean;
      extendByMonths?: number;
      baseStartDate?: Date | null;
      backoffRetryCount?: number;
      isHistoricalSearch?: boolean;
    } = {}
  ): Promise<void> => {
    const { 
      isLoadMore = false, 
      forceRefresh = false,
      extendByMonths = 0,
      baseStartDate = null,
      backoffRetryCount = 0,
      isHistoricalSearch = false
    } = options;


    // Skip if key is not ready or filter change is in progress (unless it's a force refresh)
    if (!forceRefresh && (!sessionKey || isFilterChangeInProgressRef.current)) {
      return;
    }

    // Prevent recursive auto-fetching
    if (!isLoadMore && !forceRefresh && isAutoFetchingRef.current) {
      return;
    }

    const key = sessionKey;
    if (!key) {
      return;
    }


    const currentState = getCurrentSession(key);

    // Don't fetch if we're already loading
    if (currentState.isLoading && !forceRefresh) {
      return;
    }

    // Don't fetch if we've reached the end and this isn't a force refresh or load more
    if (!forceRefresh && !isLoadMore && !currentState.hasMore && currentState.items.length > 0) {
      return;
    }

    // Calculate the adjusted start date for this request - EXACT LOGIC FROM fetch-old.tsx
    let effectiveStartDate = baseStartDate;
    if (!effectiveStartDate && currentState.startRequested) {
      // Convert ISO string back to Date object
      effectiveStartDate = new Date(currentState.startRequested);
      // If we got a full page last time, just continue from where we left off
      // Otherwise, adjust the start date based on the load type
      if (currentState.startHasMore) {
        // effectiveStartDate is already the converted Date object
      } else if (isLoadMore || backoffRetryCount > 0) {
        // effectiveStartDate is already the converted Date object
      }
    }
    
    // Calculate the effective extension amount - EXACT LOGIC FROM fetch-old.tsx
    let effectiveExtension = extendByMonths;
    if (isHistoricalSearch) {
      // For historical searches, always use LOAD_MORE_MONTHS_EXTENSION
      effectiveExtension = LOAD_MORE_MONTHS_EXTENSION;
    } else if (!currentState.startHasMore) {
      if (backoffRetryCount > 0 && BACKOFF_MULTIPLIERS[backoffRetryCount - 1]) {
        effectiveExtension = BACKOFF_MULTIPLIERS[backoffRetryCount - 1];
      }
    } else {
      effectiveExtension = 0; // Don't extend if we got a full page last time
    }

    // Get params for the API request - NOW with proper effective values
    const params = getParams(forceRefresh, effectiveExtension, effectiveStartDate);

    // Skip if we have no parameters to fetch with
    if (!params.cnf && !params.start && !params.end) {
      return;
    }

    // Skip if the adjusted start date is beyond our limit
    if (params.start) {
      const endDate = enforced_end ?? new Date();
      const monthsDiff = Math.max(0, (endDate.getTime() - params.start.getTime() - DAY_TO_MS) / MONTH_IN_MS);
      
      // For auto-loading and backoff, use normal max (6 months)
      // For manual historical searches, allow up to absolute max (12 months)
      const effectiveMaxMonths = isHistoricalSearch ? 
        ABSOLUTE_MAX_LOOKBACK_MONTHS : 
        MAX_DATE_LOOKBACK_MONTHS;
      
      if (monthsDiff > effectiveMaxMonths) {
        dispatch(updateHasMore({ key, hasMore: false }));
        
        // If this was a manual historical search and we hit the absolute max, mark it
        if (isHistoricalSearch && monthsDiff >= ABSOLUTE_MAX_LOOKBACK_MONTHS) {
          updateSessionEnhancement(key, { hitAbsoluteMaxLookback: true });
        }
        return;
      }
    }
    
    // Prevent duplicate requests
    const requestKey = JSON.stringify(params);
    if (currentState.lastRequestKey === requestKey && !forceRefresh) {
      return;
    }

    // If force refresh, clear the sessions
    if (forceRefresh) {
      dispatch(clearSessions({ key }));
      updateSessionEnhancement(key, { 
        totalItems: 0, 
        backoffRetryCount: 0, 
        noResultsAfterBackoff: false, 
        monthsSearched: 1 
      });
    }

    // Reset noResultsAfterBackoff when loading more data
    if (isLoadMore || isHistoricalSearch) {
      updateSessionEnhancement(key, { 
        noResultsAfterBackoff: false
      });
    }

    try {
      if (!isLoadMore && !forceRefresh) {
        isAutoFetchingRef.current = true;
      }

      // Start loading
      dispatch(startLoading({ key, lastRequestKey: requestKey }));
      
      // Fetch the data
      const result = await getServicesManager().getListUserSessionsV5(params);
      if (!result) { 
        return;
      }
      
      // Enrich contacts for new text sessions
      if ([SessionHistoryTabs.TEXTS, SessionHistoryTabs.MISSED].includes(tab)) {
        const addressesToEnrich = result
          .map(session => session.customer_address)
          .filter((address): address is string => 
            address !== null && 
            address !== undefined && 
            !enrichState.numberToContact[address]?.contacts &&
            !enrichState.numberToContact[address]?.loading
          );

        if (addressesToEnrich.length > 0) {
          try {
            await enrichContacts(addressesToEnrich, dispatch);
          } catch (error) {
            console.error('[SessionFetch Hook] Error enriching text session contacts:', error);
          }
        }
      }
      
      // Calculate total items after this fetch
      const totalItems = forceRefresh 
        ? result.length 
        : (currentState.items.length + result.length);
      
      // Determine if we have more items to fetch - EXACT LOGIC FROM fetch-old.tsx
      const resultFull = result.length === DEFAULT_PAGE_SIZE;

      // We have more items if either:
      // 1. We got a full page of results (normal pagination)
      // 2. We don't have a strict start date and haven't hit the max lookback period
      // 3. We're still in backoff retries and got results (but only for auto-backoff, not manual loads)
      const nextStartDate = params.start ? new Date(params.start.getTime() - MONTH_IN_MS * (isLoadMore ? 1 : LOAD_MORE_MONTHS_EXTENSION)) : null;
      const canLoadMoreDates = !hasStrictStartDate && nextStartDate && isWithinDateLimit(nextStartDate);

      // Check if we've reached the maximum lookback period FOR AUTO-LOADING (6 months)
      let reachedMaxLookback = false;
      if (params.start) {
        const endDate = enforced_end ?? new Date();
        const monthsDiff = (endDate.getTime() - params.start.getTime()) / MONTH_IN_MS;
        reachedMaxLookback = monthsDiff >= MAX_DATE_LOOKBACK_MONTHS;
      }

      // CRITICAL: hasMore logic from fetch-old.tsx
      const hasMore = resultFull || (canLoadMoreDates && !reachedMaxLookback) ||
                     (!isLoadMore && backoffRetryCount < MAX_BACKOFF_RETRIES && result.length > 0);

      // Convert to Redux-safe format before dispatching
      const reduxSafeResult = toReduxSafeSessionListResponses(result);
      // Get existing Redux-safe items from Redux state directly
      const reduxSession = sessionState.sessions[key];
      const existingReduxSafeItems = reduxSession?.items || [];
      
      // Update Redux state with the new data
      dispatch(sessionsLoaded({ 
        key,
        items: forceRefresh ? reduxSafeResult : [...existingReduxSafeItems, ...reduxSafeResult],
        startRequested: params.start ? params.start.toISOString() : null,
        startHasMore: resultFull,
        hasMore: hasMore,
        isForceRefresh: forceRefresh, 
        lastRequestKey: requestKey
      }));
      
      // Update our local enhancement tracking
      const shouldShowNoResults = 
        totalItems === 0 && 
        backoffRetryCount > 0 && 
        !hasStrictStartDate && 
        !isLoadMore;

      const actualMonthsSearched = calculateMonthsSearched(params.start);

      updateSessionEnhancement(key, {
        totalItems,
        backoffRetryCount: Math.max(currentState.backoffRetryCount || 0, backoffRetryCount),
        noResultsAfterBackoff: shouldShowNoResults,
        monthsSearched: actualMonthsSearched
      });
      
      // If this was a historical search that returned 0 results and hasMore is now false,
      // make sure we indicate we've hit the limit to prevent UI from getting stuck
      if (isHistoricalSearch && result.length === 0 && !hasMore) {
        updateSessionEnhancement(key, {
          totalItems,
          backoffRetryCount: Math.max(currentState.backoffRetryCount || 0, backoffRetryCount),
          noResultsAfterBackoff: false, // Don't show backoff message for historical searches
          monthsSearched: actualMonthsSearched,
          hitAbsoluteMaxLookback: actualMonthsSearched >= ABSOLUTE_MAX_LOOKBACK_MONTHS
        });
      }
      
      // Auto-fetch more if needed
      const isInitialRequest = !isLoadMore && !forceRefresh;
      if (isInitialRequest &&
          needsMoreData(totalItems) &&
          backoffRetryCount < MAX_BACKOFF_RETRIES &&
          !hasStrictStartDate &&
          (result.length === 0 || !resultFull)) {

        const newCurrentState = getCurrentSession(key);
        const shouldFetchMore = needsMoreData(newCurrentState.items.length);

        if (shouldFetchMore) {
          const newBackoffCount = backoffRetryCount + 1;
          const backoffDelay = BACKOFF_BASE_DELAY * Math.pow(2, backoffRetryCount);

          setTimeout(() => {
            // Verify the key is still current before fetching
            if (currentKeyRef.current !== key) {
              return;
            }

            const latestState = getCurrentSession(key);
            if (needsMoreData(latestState.items.length)) {
              fetchSessions({
                forceRefresh: false,
                isLoadMore: false,
                extendByMonths: BACKOFF_MULTIPLIERS[backoffRetryCount] || 1,
                baseStartDate: params.start,
                backoffRetryCount: newBackoffCount
              });
            }
          }, backoffDelay);
        }
      }
      
    } catch (error) {
      console.error('[SessionFetch Hook] Error fetching sessions:', error);
      dispatch(loadingFailed({ 
        key, 
        error: error instanceof Error ? error.message : 'Unknown error fetching sessions' 
      }));
    } finally {
      if (!isLoadMore && !forceRefresh) {
        isAutoFetchingRef.current = false;
      }
    }
  }, [
    sessionKey,
    enforced_end,
    hasStrictStartDate,
    getCurrentSession,
    getParams,
    isWithinDateLimit,
    dispatch,
    needsMoreData,
    updateSessionEnhancement,
    calculateMonthsSearched,
    tab,
    enrichState,
    sessionState.sessions
  ]);

  // Debounced key generation to prevent rapid filter changes from causing cascading fetches
  const [debouncedKey, setDebouncedKey] = useState<string>('');
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  
  // Effect to handle debounced key updates
  useEffect(() => {
    const rawKey = sessionKey;
    
    // If debouncing is disabled, update immediately
    if (disableDebouncing) {
      setDebouncedKey(rawKey);
      currentKeyRef.current = rawKey;
      setIsDebouncing(false);
      isFilterChangeInProgressRef.current = false;
      if (rawKey) {
        dispatch(stopDebouncing({ key: rawKey }));
      }
      return;
    }
    
    // Clear existing debounce timers
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    if (keyStabilizationRef.current) {
      clearTimeout(keyStabilizationRef.current);
    }
    
    // Set flags to indicate filter change is in progress
    isFilterChangeInProgressRef.current = true;
    setIsDebouncing(true);
    
    // Dispatch debouncing start
    if (rawKey) {
      dispatch(startDebouncing({ key: rawKey }));
    }
    
    // First debounce - wait for filter changes to stabilize
    filterDebounceRef.current = setTimeout(() => {
      // Second debounce - ensure key is still stable
      keyStabilizationRef.current = setTimeout(() => {
        const finalKey = sessionKey;
        if (finalKey === rawKey) {
          setDebouncedKey(finalKey);
          currentKeyRef.current = finalKey;
          isFilterChangeInProgressRef.current = false;
          setIsDebouncing(false);
          if (finalKey) {
            dispatch(stopDebouncing({ key: finalKey }));
          }
        }
      }, KEY_STABILIZATION_MS);
    }, FILTER_DEBOUNCE_MS);
    
    // Cleanup function
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
      if (keyStabilizationRef.current) {
        clearTimeout(keyStabilizationRef.current);
      }
    };
  }, [sessionKey, disableDebouncing, dispatch]);

  // Use the debounced key as the current key for fetching
  const currentKey = debouncedKey;

  // Track key changes with debounced logic
  useEffect(() => {
    if (!currentKey || isFilterChangeInProgressRef.current) {
      return;
    }

    if (isInitialMount.current) {
      return;
    }
    
    if (previousKeyRef.current !== currentKey) {
      previousKeyRef.current = currentKey;
      hasKeyChanged.current = true;
    }
  }, [currentKey]);

  // Handle initial load and key changes with debouncing
  useEffect(() => {
    if (isAutoFetchingRef.current || isFilterChangeInProgressRef.current) {
      return;
    }

    if (!currentKey) {
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousKeyRef.current = currentKey;
      
      // Check if we already have data for this key before fetching
      const currentState = getCurrentSession(currentKey);
      const hasExistingData = currentState.items && currentState.items.length > 0;
      
      if (!hasExistingData) {
        fetchSessions({ forceRefresh: false });
      }
    } else if (hasKeyChanged.current) {
      // Check if we already have data for this key
      const currentState = getCurrentSession(currentKey);
      const hasExistingData = currentState.items && currentState.items.length > 0;
      
      if (hasExistingData) {
        hasKeyChanged.current = false; // Reset the flag
      } else {
      fetchSessions({ forceRefresh: false });
      hasKeyChanged.current = false;
    }
    }
  }, [currentKey, fetchSessions, getCurrentSession]);

  // Handle force refresh
  useEffect(() => {
    if (refreshCount === 0) return;
    if (refreshCount === lastProcessedRefreshRef.current) {
      return;
    }
    
    if (isRefreshingRef.current) {
      lastProcessedRefreshRef.current = refreshCount;
      return;
    }
    
    lastProcessedRefreshRef.current = refreshCount;
    isRefreshingRef.current = true;
    
    fetchSessions({ forceRefresh: true })
      .finally(() => {
        setTimeout(() => {
          isRefreshingRef.current = false;
        }, 500);
      });
  }, [refreshCount, fetchSessions]);


  // Public methods
  const loadMore = useCallback((fromInfiniteScroll: boolean = false) => {
    if (!fromInfiniteScroll) {
      const currentState = getCurrentSession(currentKey);
      const currentMonthsSearched = currentState.monthsSearched || 1;

      if (currentMonthsSearched >= ABSOLUTE_MAX_LOOKBACK_MONTHS) {
        return;
      }
    }

    const extendByMonths = fromInfiniteScroll ? 1 : LOAD_MORE_MONTHS_EXTENSION;
    const minimumLoadingTime = 500;
    const startTime = Date.now();

    fetchSessions({
      isLoadMore: true,
      extendByMonths: extendByMonths,
      backoffRetryCount: 0,
      isHistoricalSearch: !fromInfiniteScroll
    }).finally(() => {
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minimumLoadingTime) {
        setTimeout(() => {
          // Placeholder for completion timing
        }, minimumLoadingTime - elapsedTime);
      }
    });
  }, [fetchSessions, currentKey, getCurrentSession]);

  const refresh = useCallback(() => {
    if (isRefreshingRef.current) {
      return;
    }
    
    isRefreshingRef.current = true;
    fetchSessions({ forceRefresh: true })
      .finally(() => {
        setTimeout(() => {
          isRefreshingRef.current = false;
        }, 500);
      });
  }, [fetchSessions]);

  // Cleanup function to prevent memory leaks and race conditions
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = null;
      }
      if (keyStabilizationRef.current) {
        clearTimeout(keyStabilizationRef.current);
        keyStabilizationRef.current = null;
      }
      
      isFilterChangeInProgressRef.current = false;
      isAutoFetchingRef.current = false;
      isRefreshingRef.current = false;
    };
  }, []);

  // Improved items memoization with stable augmentation
  const items = useMemo(() => {
    if (!baseItems) return [];
    
    switch (tab) {  
      case SessionHistoryTabs.TEXTS:
      case SessionHistoryTabs.MISSED:
        return baseItems.map(item => {
          try {
            return augmentSessionWithPersonData(item, enrichState);
          } catch (error) {
            console.error('[SessionFetch Hook] Error augmenting session data:', error);
            return item;
          }
        });
      default:
        return baseItems;
    }
  }, [baseItems, tab, enrichState]);

  return {
    // All the data from session-items-hook
    ...sessionItems,
    
    // Override items with augmented version
    items: items,
    
    // Add fetch-specific functionality
    loadMore,
    refresh,
    isDebouncing: isDebouncing || sessionItems.isDebouncing,
    // Consolidated loading state that includes debouncing
    isLoadingOrDebouncing: sessionItems.isLoadingOrDebouncing || isDebouncing
  };
}

// Export useSessionList as an alias for backward compatibility
export const useSessionList = useSessionFetch;
